import { noOp } from 'common/Constants';
import { formatCurrency } from 'common/FormatUtilities';
import { lt } from 'common/PatternMatchingUtilities';
import { assertDateString } from 'common/UrlDate';
import { compact, concat, isNil, keyBy } from 'lodash';
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { CustomizationTags } from 'src/shared/design-system/CustomizationTags';
import { DynamicImage } from 'src/shared/design-system/DynamicImage';
import { Paragraph } from 'src/shared/design-system/Text';
import { useLocation } from 'src/shared/hooks/useLocation';
import { getSelectedServingSize, getSelectedSpiceLevel } from 'src/shared/utils/CustomizationUtils';
import { useDispatch } from 'src/store';
import { decLineItemQuantity, incLineItemQuantity, removeLineItem } from 'src/store/cart/actions';
import { WholeCartLineItem } from 'src/store/cart/types';
import styled from 'styled-components';
import { match, P } from 'ts-pattern';
import { Colors, Font, FontSize, FontWeight, Spacing } from 'web-common/src/shared/styles';
import { ExploreDishModalWithZip } from '../ExploreDishModalWithZip';
import {
  imageSizePx,
  ItemImageContainer,
  ItemLeftColContainer,
  ItemRightColContainer,
  ItemRowContainer,
} from './MultiCartItem';
import { MultiCartQuantityPicker } from './MultiCartQuantityPicker';

const TrashButton = styled.button`
  background: none;
  border: none;

  // make touch target bigger without affecting position within design
  padding: 8px;
  margin-top: -8px;
  margin-right: -8px;
`;

const SoldOutMessage = styled.div`
  margin-top: 16px;
  text-align: center;
`;

const SoldOutSimilarLink = styled(Link)`
  color: ${Colors.RADISH};
  font-family: ${Font.Family.SANS};
  font-size: 16px;
  font-weight: 600;
  text-decoration: underline;

  &:hover {
    color: ${Colors.BELL_PEPPER_DARK_10};
    text-decoration: underline;
  }
`;

const LineItemTitle = styled.button`
  font-size: ${FontSize.S};
  font-weight: ${FontWeight.SEMI_BOLD};

  margin: 0;
  padding: 0;

  border: none;
  background: none;
  text-align: left;
`;

enum LineItemState {
  SHEF_SOLD_OUT = 'SHEF_SOLD_OUT',
  SHEF_AT_CAPACITY = 'SHEF_AT_CAPACITY',
  SHEF_OVER_CAPACITY = 'SHEF_OVER_CAPACITY',
  ITEM_SOLD_OUT = 'ITEM_SOLD_OUT',
  ITEM_OVER_CAPACITY = 'ITEM_OVER_CAPACITY',
  DEFAULT = 'DEFAULT',
}

interface GetLineItemStateParams {
  shefNumDishesAvailable: number;
  shefNumDishesRemaining: number;
  numAvailable: number | null;
  numRemaining: number | null;
}

const getLineItemState = (params: GetLineItemStateParams): LineItemState =>
  // in order of precedence
  match(params)
    .with({ shefNumDishesAvailable: 0 }, () => LineItemState.SHEF_SOLD_OUT)
    .with({ shefNumDishesRemaining: lt(0) }, () => LineItemState.SHEF_OVER_CAPACITY)
    .with({ numAvailable: 0 }, () => LineItemState.ITEM_SOLD_OUT)
    .with({ numRemaining: P.intersection(P.number, lt(0)) }, () => LineItemState.ITEM_OVER_CAPACITY)
    .with({ shefNumDishesRemaining: 0 }, () => LineItemState.SHEF_AT_CAPACITY)
    .otherwise(() => LineItemState.DEFAULT);
interface MutliCartLineItemProps {
  deliveryDate: string;
  lineItem: WholeCartLineItem;
  shefId: string;
  unavailable: boolean;
  numAvailable: number;
  disabled?: boolean;
  hideQuantityPicker?: boolean;
  shefMenuUrl: string;
  shefNumDishesAvailable: number;
  shefNumDishesRemaining: number;
  isEditingOrder: boolean;
  paddingX?: string;
  paddingY?: string;
}

export const MultiCartLineItem: React.FC<MutliCartLineItemProps> = ({
  deliveryDate,
  lineItem,
  shefId,
  numAvailable,
  disabled = false,
  hideQuantityPicker = false,
  shefMenuUrl,
  shefNumDishesAvailable,
  shefNumDishesRemaining,
  isEditingOrder,
  paddingX = Spacing.NONE,
  paddingY = Spacing.NONE,
}) => {
  const dispatch = useDispatch();
  const increment = () =>
    dispatch(incLineItemQuantity(isEditingOrder, { foodItem: lineItem.foodItem, deliveryDate, location: 'cart' }));
  const decrement = () =>
    dispatch(decLineItemQuantity(isEditingOrder, { foodItem: lineItem.foodItem, deliveryDate, location: 'cart' }));
  const remove = () =>
    dispatch(removeLineItem(isEditingOrder, { foodItem: lineItem.foodItem, deliveryDate, location: 'cart' }));

  const numRemaining = numAvailable - lineItem.quantity;
  const location = useLocation();
  const state = getLineItemState({
    shefNumDishesAvailable,
    shefNumDishesRemaining,
    numAvailable,
    numRemaining,
  });

  const [editModalShowing, setEditModalShowing] = useState(false);
  const handleClose = useCallback(() => setEditModalShowing(false), [setEditModalShowing]);

  const [quantityMessageColor, quantityMessage] = match(numRemaining)
    .with(lt(0), (num) => [Colors.RADISH, `Remove ${Math.abs(num)}`])
    .with(0, () => [Colors.STRAWBERRY_600, `Max quantity`])
    .with(1, () => [Colors.STRAWBERRY_600, `1 left!`])
    .with(2, () => [Colors.STRAWBERRY_600, `2 left!`])
    .with(3, () => [Colors.STRAWBERRY_600, `3 left!`])
    .otherwise(() => ['', '']);

  const itemSoldOut = state === LineItemState.ITEM_SOLD_OUT;
  const shefSoldOut = state === LineItemState.SHEF_SOLD_OUT;
  const shefAtCapacity = state === LineItemState.SHEF_AT_CAPACITY;
  const shefOverCapacity = state === LineItemState.SHEF_OVER_CAPACITY;

  const showQuantityPicker = !hideQuantityPicker && !(itemSoldOut || shefSoldOut);
  const showLimitedQuantityMessage =
    !!quantityMessage && !shefSoldOut && !itemSoldOut && !shefOverCapacity && !shefAtCapacity;
  const showSoldOutSimilarLink = shefMenuUrl !== location.pathname;
  const dimImage = itemSoldOut || shefSoldOut;
  const incrementDisabled = shefAtCapacity || shefOverCapacity;
  const bgColor = match(state)
    .with(LineItemState.SHEF_AT_CAPACITY, () => Colors.ELDERBERRY_LITE_30)
    .with(
      LineItemState.ITEM_SOLD_OUT,
      LineItemState.ITEM_OVER_CAPACITY,
      LineItemState.SHEF_SOLD_OUT,
      LineItemState.SHEF_OVER_CAPACITY,
      () => Colors.BELL_PEPPER_LITE_30
    )
    .otherwise(() => 'white');
  const strikeThroughPrice = itemSoldOut || shefSoldOut;

  const { foodItem } = lineItem;
  const allCustomizations = compact(concat(foodItem.spiceLevelCustomizations, foodItem.servingSizeCustomizations));
  const keyedCustomizations = keyBy(allCustomizations, 'id');

  const selectedCustomizations = !isNil(lineItem.customizationIds)
    ? lineItem.customizationIds
        .map((customizationId) => keyedCustomizations[customizationId])
        .filter((customization) => !isNil(customization))
    : [];

  // Show customization option if the food item is customizeable
  const selectedSpiceLevel = getSelectedSpiceLevel({ selectedCustomizations, foodItem });
  const hasSpiceLevelCustomizations = (foodItem.spiceLevelCustomizations?.length ?? 0) > 0;
  const spiceLevelToShow = hasSpiceLevelCustomizations ? selectedSpiceLevel : undefined;

  const selectedServingSize = getSelectedServingSize({ selectedCustomizations, foodItem });
  const hasServingSizeCustomizations = (foodItem.servingSizeCustomizations?.length ?? 0) > 0;
  const servingSizeToShow = hasServingSizeCustomizations ? selectedServingSize : undefined;

  return (
    <div style={{ background: bgColor, padding: `${paddingY} ${paddingX}` }}>
      <ItemRowContainer>
        <ItemLeftColContainer onClick={() => setEditModalShowing(true)}>
          <ItemImageContainer>
            <DynamicImage
              style={{ opacity: dimImage ? 0.5 : 1 }}
              width={`${imageSizePx}px`}
              height={`${imageSizePx}px`}
              sourceHeight={imageSizePx}
              src={lineItem.foodItem.imageUrl ?? ''}
              alt={lineItem.foodItem.name ?? ''}
            />
          </ItemImageContainer>
          <div>
            <LineItemTitle>{lineItem.foodItem.name}</LineItemTitle>
            <Paragraph
              size='14px'
              margin='0'
              strikeThrough={strikeThroughPrice}
              color={Colors.GRAY_DARK_20}
              weight={FontWeight.SEMI_BOLD}>
              {formatCurrency(lineItem.unitPriceWithCustomizations)}
            </Paragraph>
            <div className='flex flex-row justify-start gap-2'>
              <CustomizationTags spiceLevel={spiceLevelToShow} servingSize={servingSizeToShow} />
            </div>
          </div>
        </ItemLeftColContainer>
        <ItemRightColContainer>
          {showQuantityPicker && (
            <MultiCartQuantityPicker
              disabled={disabled}
              decrement={decrement}
              increment={increment}
              incrementDisabled={incrementDisabled}
              quantity={lineItem.quantity}
              maxQuantity={numAvailable || undefined}
            />
          )}
          {showLimitedQuantityMessage && (
            <Paragraph color={quantityMessageColor} size='14px' weight='600' italic margin='8px 0 0 0'>
              {quantityMessage}
            </Paragraph>
          )}
          {itemSoldOut && (
            <TrashButton onClick={remove}>
              <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                  d='M5.21875 0.5625C5.375 0.21875 5.71875 0 6.09375 0H9.875C10.25 0 10.5938 0.21875 10.75 0.5625L11 1H14C14.5312 1 15 1.46875 15 2C15 2.5625 14.5312 3 14 3H2C1.4375 3 1 2.5625 1 2C1 1.46875 1.4375 1 2 1H5L5.21875 0.5625ZM1.96875 4H14V14C14 15.125 13.0938 16 12 16H3.96875C2.875 16 1.96875 15.125 1.96875 14V4ZM4.46875 6.5V13.5C4.46875 13.7812 4.71875 14 4.96875 14C5.25 14 5.46875 13.7812 5.46875 13.5V6.5C5.46875 6.25 5.25 6 4.96875 6C4.71875 6 4.46875 6.25 4.46875 6.5ZM7.46875 6.5V13.5C7.46875 13.7812 7.71875 14 7.96875 14C8.25 14 8.5 13.7812 8.5 13.5V6.5C8.5 6.25 8.25 6 7.96875 6C7.71875 6 7.46875 6.25 7.46875 6.5ZM10.5 6.5V13.5C10.5 13.7812 10.7188 14 11 14C11.25 14 11.5 13.7812 11.5 13.5V6.5C11.5 6.25 11.25 6 11 6C10.7188 6 10.5 6.25 10.5 6.5Z'
                  fill='#383838'
                />
              </svg>
            </TrashButton>
          )}
        </ItemRightColContainer>
      </ItemRowContainer>
      {itemSoldOut && (
        <SoldOutMessage>
          <Paragraph size='16px' color={Colors.RADISH} margin='0'>
            <svg
              width='17'
              height='15'
              viewBox='0 0 17 15'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              style={{ marginRight: 8 }}>
              <path
                d='M16.3123 12.5312L9.65601 1.15625C9.12476 0.28125 7.84351 0.28125 7.34351 1.15625L0.656006 12.5312C0.156006 13.4062 0.781006 14.5 1.81226 14.5H15.156C16.1873 14.5 16.8123 13.4062 16.3123 12.5312ZM7.74976 4.75C7.74976 4.34375 8.06226 4 8.49976 4C8.90601 4 9.24976 4.34375 9.24976 4.75V8.75C9.24976 9.1875 8.90601 9.5 8.49976 9.5C8.12476 9.5 7.74976 9.1875 7.74976 8.75V4.75ZM8.49976 12.5C7.93726 12.5 7.49976 12.0625 7.49976 11.5312C7.49976 11 7.93726 10.5625 8.49976 10.5625C9.03101 10.5625 9.46851 11 9.46851 11.5312C9.46851 12.0625 9.03101 12.5 8.49976 12.5Z'
                fill='#EC2044'
              />
            </svg>
            This item is no longer available
          </Paragraph>
          {showSoldOutSimilarLink && <SoldOutSimilarLink to={shefMenuUrl}>See similar items</SoldOutSimilarLink>}
        </SoldOutMessage>
      )}
      {editModalShowing && (
        <ExploreDishModalWithZip
          deliveryDate={assertDateString(deliveryDate)}
          cartActionLocation='cart'
          foodItem={lineItem.foodItem}
          quantity={lineItem.quantity}
          remainingQuantity={shefNumDishesRemaining}
          shefId={shefId}
          isShowing={editModalShowing}
          isEditingOrder={isEditingOrder}
          openModal={noOp}
          onClose={handleClose}
          showModalPrompt={false}
        />
      )}
    </div>
  );
};
